// modalNovoChamado.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import SelectCliente from "../components/ui/selectCliente";
import { api } from "../api";
import "../sass/components/modalNovoChamado.scss";
import { showInfo, showError } from "../utils/toast";
import ModalPlanos from "./modalPlanos";

const STATUS_MAP = {
  "0": { label: "Em aberto", tone: "warning" },
  "1": { label: "Finalizado", tone: "success" },
  "": { label: "Em atendimento", tone: "info" },
  null: { label: "Em atendimento", tone: "info" },
  undefined: { label: "Em atendimento", tone: "info" },
};
const statusInfo = (v) => {
  const key = (v ?? "").toString().trim();
  return STATUS_MAP[key] || { label: key || "Indefinido", tone: "neutral" };
};

const parseBR = (s) => {
  if (!s) return null;
  const str = String(s).trim();
  const m = str.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (!m) return null;
  const [, dd, mm, yyyy, HH = "00", MM = "00", SS = "00"] = m;
  return new Date(`${yyyy}-${mm}-${dd}T${HH}:${MM}:${SS}`);
};

const moneyBR = (v) => {
  const n = Number(String(v ?? "0").replace(",", "."));
  if (!Number.isFinite(n)) return "0,00";
  return n.toFixed(2).replace(".", ",");
};

const getSistemaInfo = (cliente) => {
  if (!cliente) return null;
  const raw = cliente.ServidorWeb ?? cliente.TipoSistema ?? cliente.tipoSistema ?? "";
  const s = String(raw).trim().toLowerCase();
  if (s === "spw") return { key: "spw", label: "SPW" };
  if (s === "local") return { key: "local", label: "Sistema Local" };
  if (s === "web") return { key: "web", label: "Sistema Web" };
  if (!s) return null;
  return { key: s, label: raw };
};

const rangeUltimos5Meses = () => {
  const hoje = new Date();
  const start = new Date(hoje.getFullYear(), hoje.getMonth() - 4, 1); // mês atual + 4 anteriores
  const end = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0, 23, 59, 59); // fim do mês atual
  return { start, end };
};

const normStr = (value) => (value == null ? "" : String(value).trim());

const normSetor = (value) => {
  const setor = normStr(value);
  return setor ? setor.toUpperCase() : "SUPORTE";
};

const isClienteInativo = (cliente) => {
  if (!cliente) return false;

  const tipCad = normStr(cliente?.TipCad ?? cliente?.tipCad).toLowerCase();
  const desativado = normStr(
    cliente?.Desativado ?? cliente?.desativado,
  ).toLowerCase();

  return (
    tipCad === "cliente desistente" ||
    tipCad === "cliente_desistente" ||
    ["true", "1", "s", "sim"].includes(desativado)
  );
};

const getSessionObject = (key) => {
  try {
    return JSON.parse(sessionStorage.getItem(key) || "{}");
  } catch {
    return {};
  }
};

const getSessionFirst = (...keys) => {
  for (const key of keys) {
    const value = sessionStorage.getItem(key);
    if (normStr(value)) return normStr(value);
  }
  return "";
};

const getUsuarioSessao = () => {
  const user = getSessionObject("user");

  return {
    codFunc: normStr(
      user.codFunc ??
        user.CodFunc ??
        user.codigoFuncionario ??
        getSessionFirst("codFunc", "CodFunc", "CODFUNC")
    ),
    funcionario: normStr(
      user.RazaoSocial ??
        user.razaoSocial ??
        user.Nome ??
        user.nome ??
        getSessionFirst("RazaoSocial", "funcionario", "Nome")
    ),
    nivel: normStr(
      user.NivelFunc ??
        user.NiveFunc ??
        user.Nivel ??
        user.nivel ??
        getSessionFirst("NivelFunc", "NiveFunc", "Nivel")
    ).toUpperCase(),
    setor: normSetor(
      user.setor ??
        user.Setor ??
        user.SETOR ??
        getSessionFirst("setor", "Setor", "SETOR")
    ),
  };
};

const NovoChamadoModal = ({ onClose, onSuccess, clienteInicial }) => {
  const [nivelFunc, setNivelFunc] = useState("");
  const [problemas, setProblemas] = useState([]);
  const [subproblemas, setSubproblemas] = useState([]);
  const [setores, setSetores] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);

  const [mostrarModalPlanos, setMostrarModalPlanos] = useState(false);
  const [planosCliente, setPlanosCliente] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Financeiro + Histórico no próprio modal
  const [parcelasRaw, setParcelasRaw] = useState({ parcelas: [], totalGeral: "0,00", totalAtrasado: "0,00" });
  const [isLoadingParcelas, setIsLoadingParcelas] = useState(false);

  const [historicoChamados, setHistoricoChamados] = useState([]);
  const [isLoadingHistorico, setIsLoadingHistorico] = useState(false);
  const [histSelecionado, setHistSelecionado] = useState(null);
  const [mostrarMaisHistorico, setMostrarMaisHistorico] = useState(false);

  const abortRef = useRef({ parcelas: null, historico: null, planos: null });
  const reqSeq = useRef(0);

  const [form, setForm] = useState({
    contato: "",
    razaoSocial: "",
    nomeFantasia: "",
    codigoCad: "",
    problema: "",
    subproblema: "",
    setor: "",
    descricao: "",
    urgencia: "",
    emAtendimento: false,
    agendamento: false,
  });

  // UX: não permitir fechamento acidental com dados já preenchidos.
  // Clique fora do modal nunca fecha; ESC só fecha enquanto o formulário ainda está vazio.
  const temRascunho = useMemo(() => {
    return (
      Boolean(normStr(form.contato)) ||
      Boolean(normStr(form.problema)) ||
      Boolean(normStr(form.subproblema)) ||
      Boolean(normStr(form.descricao)) ||
      form.urgencia !== "" ||
      Boolean(form.emAtendimento) ||
      Boolean(form.agendamento)
    );
  }, [
    form.contato,
    form.problema,
    form.subproblema,
    form.descricao,
    form.urgencia,
    form.emAtendimento,
    form.agendamento,
  ]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;

      e.preventDefault();

      if (isSubmitting || temRascunho) return;

      onClose?.();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, isSubmitting, temRascunho]);

  const montarClienteNormalizado = (clienteBase = {}) => {
    const cliente = clienteBase?.data || clienteBase;
    const codigoCad =
      cliente?.CodigoCad?.toString?.() ||
      cliente?.codigoCad?.toString?.() ||
      cliente?.codigo?.toString?.() ||
      cliente?.ChvCad?.toString?.() ||
      cliente?.chvCad?.toString?.() ||
      cliente?.value?.toString?.() ||
      "";

    const razaoSocial =
      cliente?.RazaoSocial ||
      cliente?.razaoSocial ||
      cliente?.razao ||
      cliente?.nome ||
      cliente?.label ||
      "";

    const nomeFantasia =
      cliente?.NomeFant ||
      cliente?.NomeFantasia ||
      cliente?.nomeFantasia ||
      cliente?.fantasia ||
      "";

    return {
      ...cliente,
      CodigoCad: codigoCad,
      value: codigoCad,
      label: codigoCad
        ? `${codigoCad} - ${razaoSocial || nomeFantasia || "Cliente"}`
        : razaoSocial || nomeFantasia || "Cliente",
      RazaoSocial: razaoSocial,
      NomeFant: nomeFantasia,
      CpfCnpj: cliente?.CpfCnpj || cliente?.cpfCnpj || cliente?.cnpj || "",
    };
  };

  // Cliente recebido da tela de Chamados
  useEffect(() => {
    if (!clienteInicial) return;

    let cancelado = false;

    const aplicarCliente = (clienteBase) => {
      if (cancelado || !clienteBase) return false;

      const clienteNormalizado = montarClienteNormalizado(clienteBase);

      if (isClienteInativo(clienteNormalizado)) {
        setClienteSelecionado(null);
        setForm((prev) => ({
          ...prev,
          razaoSocial: "",
          nomeFantasia: "",
          codigoCad: "",
        }));
        showInfo(
          "Cliente inativo/desistente não está disponível para abertura de chamado.",
        );
        return false;
      }

      setClienteSelecionado(clienteNormalizado);
      setForm((prev) => ({
        ...prev,
        razaoSocial: clienteNormalizado.RazaoSocial || "",
        nomeFantasia: clienteNormalizado.NomeFant || "",
        codigoCad: clienteNormalizado.CodigoCad || "",
      }));
      return true;
    };

    const clienteParcial = montarClienteNormalizado(clienteInicial);
    const clienteParcialAceito = aplicarCliente(clienteParcial);

    if (clienteParcialAceito !== false && clienteParcial.CodigoCad) {
      api
        .get("/cliente/por-codigo", {
          params: { codigo: clienteParcial.CodigoCad, incluirDesativados: 0 },
        })
        .then((res) => aplicarCliente({ ...clienteParcial, ...(res?.data || {}) }))
        .catch(() => null);
    }

    return () => {
      cancelado = true;
    };
  }, [clienteInicial]);

  useEffect(() => {
    const usuario = getUsuarioSessao();
    const nivel = usuario.nivel || "";
    const setorPadrao = usuario.setor || "SUPORTE";

    setNivelFunc(nivel);
    setSetores([
      "FINANCEIRO",
      "PROGRAMAÇÃO",
      "SUPORTE",
      "HARDWARE",
      "SERVIÇOS GERAIS",
      "ENTREGADOR",
    ]);

    setForm((prev) => ({
      ...prev,
      setor: nivel === "ADM" ? prev.setor : setorPadrao,
    }));

    carregarProblemas();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "problema") carregarSubproblemas(value);
  };

  const carregarProblemas = async () => {
    try {
      const res = await api.get("/problemas");
      setProblemas(res.data || []);
    } catch (err) {
      console.error("Erro ao carregar problemas:", err?.message || err);
    }
  };

  const carregarSubproblemas = async (problema) => {
    if (!problema) {
      setSubproblemas([]);
      return;
    }
    try {
      const res = await api.get("/subproblemas", { params: { problema } });
      setSubproblemas((res.data || []).map((item) => item.SubProblema));
    } catch (err) {
      console.error("Erro ao carregar subproblemas:", err?.message || err);
      setSubproblemas([]);
    }
  };

  const buscarPlanos = async () => {
    if (!clienteSelecionado?.CodigoCad) return;
    try {
      const res = await api.get("/cliente/planos", {
        params: { codigo: clienteSelecionado.CodigoCad },
      });
      setPlanosCliente(res.data || []);
      setMostrarModalPlanos(true);
    } catch (err) {
      showError("Erro ao buscar planos.");
    }
  };

  const buscarClientesPorCodigo = async (codigo) => {
    const codigoLimpo = normStr(codigo);

    if (!codigoLimpo) {
      showError("Informe o código do cadastro.");
      return;
    }

    try {
      const res = await api.get("/cliente/por-codigo", {
        params: { codigo: codigoLimpo, incluirDesativados: 0 },
      });
      const cliente = res.data ?? null;

      if (cliente) {
        const clienteNormalizado = montarClienteNormalizado(cliente);

        if (isClienteInativo(clienteNormalizado)) {
          setClienteSelecionado(null);
          setForm((prev) => ({
            ...prev,
            razaoSocial: "",
            nomeFantasia: "",
          }));
          showInfo(
            "Cliente inativo/desistente não está disponível para abertura de chamado.",
          );
          return;
        }

        setForm((prev) => ({
          ...prev,
          razaoSocial: clienteNormalizado.RazaoSocial || "",
          nomeFantasia: clienteNormalizado.NomeFant || "",
          codigoCad: clienteNormalizado.CodigoCad || "",
        }));
        setClienteSelecionado(clienteNormalizado);
      } else {
        setClienteSelecionado(null);
        showError("Cliente não encontrado para o código informado.");
      }
    } catch (err) {
      console.error("Erro ao buscar cliente por código:", err?.message || err);
      showError("Erro ao buscar cliente por código.");
    }
  };

  const handleClear = () => {
    const usuario = getUsuarioSessao();
    const setorPadrao = usuario.setor || "SUPORTE";
    const nivelAtual = nivelFunc || usuario.nivel || "";

    setForm({
      contato: "",
      razaoSocial: "",
      nomeFantasia: "",
      codigoCad: "",
      problema: "",
      subproblema: "",
      setor: nivelAtual === "ADM" ? "" : setorPadrao,
      descricao: "",
      urgencia: "",
      emAtendimento: false,
      agendamento: false,
    });
    setClienteSelecionado(null);
    setParcelasRaw({ parcelas: [], totalGeral: "0,00", totalAtrasado: "0,00" });
    setHistoricoChamados([]);
    setHistSelecionado(null);
    setMostrarMaisHistorico(false);
  };

  const validarFormulario = ({ codigoCad, setorFinal }) => {
    if (!clienteSelecionado || !codigoCad) return "Selecione um cliente válido.";
    if (isClienteInativo(clienteSelecionado)) {
      return "Cliente inativo/desistente não pode ser utilizado para abrir chamado.";
    }
    if (!normStr(form.contato)) return "Informe o nome do contato.";
    if (!normStr(form.problema)) return "Selecione o problema.";
    if (!normStr(form.subproblema)) return "Selecione o subproblema.";
    if (!normStr(setorFinal)) return "Selecione o setor.";
    if (form.urgencia === "") return "Selecione a urgência.";
    if (!normStr(form.descricao)) return "Informe a descrição do atendimento.";
    return "";
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const usuario = getUsuarioSessao();
    const codigoCad = normStr(
      clienteSelecionado?.CodigoCad ||
        clienteSelecionado?.value ||
        form.codigoCad
    );
    const setorFinal = normSetor(form.setor || usuario.setor || "SUPORTE");
    const erroValidacao = validarFormulario({ codigoCad, setorFinal });

    if (erroValidacao) {
      showError(erroValidacao);
      return;
    }

    const urgenciaLegacy = String(parseInt(form.urgencia, 10) + 1); // UI 0..4 → legado 1..5

    const payload = {
      contato: normStr(form.contato),
      razaoSocial: normStr(form.razaoSocial || clienteSelecionado?.RazaoSocial),
      nomeFantasia: normStr(form.nomeFantasia || clienteSelecionado?.NomeFant),
      problema: normStr(form.problema),
      subproblema: normStr(form.subproblema),
      setor: setorFinal,
      descricao: normStr(form.descricao),
      urgencia: urgenciaLegacy,
      codfunc: usuario.codFunc,
      codFunc: usuario.codFunc,
      func: usuario.funcionario,
      tecRespChamado: form.emAtendimento ? usuario.funcionario : "",
      tecAtendimento: form.emAtendimento ? usuario.funcionario : "",
      atendimento: form.emAtendimento ? "True" : "False",
      nivelChamado: "0",
      agendamento: form.agendamento ? "1" : "0",
      resolvidoNao: "0",
      chvCad: codigoCad,
      codigoCad,
      cnpj: normStr(clienteSelecionado?.CpfCnpj),
      cpfCnpj: normStr(clienteSelecionado?.CpfCnpj),
      status: "0",
    };

    if (!payload.codfunc || !payload.func) {
      showError("Sessão inválida. Faça login novamente.");
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await api.post("/chamados/novo", payload);

      if (res.status === 201 || res.data?.sucesso) {
        showInfo("Chamado incluído com sucesso!");
        onSuccess?.({ emAtendimento: form.emAtendimento, setor: setorFinal });
        onClose?.();
      } else {
        showError(res.data?.mensagem || res.data?.message || "Erro ao incluir chamado.");
      }
    } catch (err) {
      console.error("Erro ao enviar chamado:", {
        status: err?.response?.status,
        data: err?.response?.data,
        payload,
        message: err?.message,
      });

      showError(
        err?.response?.data?.mensagem ||
          err?.response?.data?.message ||
          err?.response?.data?.erro ||
          "Erro ao incluir chamado. Verifique os campos obrigatórios."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------- Helpers de UI ----------
  const sistemaInfo = useMemo(() => getSistemaInfo(clienteSelecionado), [clienteSelecionado]);

  const statusClass =
    (clienteSelecionado?.TipCad || "").toLowerCase() === "cliente desistente"
      ? "status-badge desistente"
      : "status-badge ativo";

  const dadosClienteGrid = useMemo(() => {
    if (!clienteSelecionado) return [];
    const joinEnd = [clienteSelecionado.enderecoLinha1, clienteSelecionado.enderecoLinha2]
      .filter(Boolean)
      .join(" ");

    return [
      { label: "Razão Social", value: clienteSelecionado.RazaoSocial },
      { label: "Fantasia", value: clienteSelecionado.NomeFant },
      { label: "CNPJ", value: clienteSelecionado.CpfCnpj },
      { label: "Chave", value: clienteSelecionado.CodigoCad },
      { label: "Telefones", value: clienteSelecionado.telefones, span2: true },
      { label: "Máquinas", value: clienteSelecionado.NumMaquina },
      { label: "Endereço", value: joinEnd, span2: true },
      { label: "Tipo", value: clienteSelecionado.Comercio },
      { label: "Observações", value: clienteSelecionado.Obs, span2: true },
      { label: "Restrições", value: clienteSelecionado.Restricao, span2: true },
    ].filter((x) => x.value != null && String(x.value).trim() !== "");
  }, [clienteSelecionado]);

  // ---------- Carregar Parcelas (últimos 5 meses) e Histórico ao selecionar cliente ----------
  const carregarFinanceiroEHistorico = useCallback(async (codigoCad) => {
    if (!codigoCad) return;

    // abort requests anteriores
    abortRef.current.parcelas?.abort?.();
    abortRef.current.historico?.abort?.();

    const seq = ++reqSeq.current;

    const acParcelas = new AbortController();
    const acHistorico = new AbortController();
    abortRef.current.parcelas = acParcelas;
    abortRef.current.historico = acHistorico;

    setIsLoadingParcelas(true);
    setIsLoadingHistorico(true);
    setHistSelecionado(null);
    setMostrarMaisHistorico(false);

    const { start, end } = rangeUltimos5Meses();
    const dataInicio = start.toISOString().split("T")[0];
    const dataFim = end.toISOString().split("T")[0];

    try {
      const [parcelasRes, historicoRes] = await Promise.all([
        api.get("/cliente/parcelas", {
          params: { codigo: codigoCad },
          signal: acParcelas.signal,
        }),
        api.get(`/chamados/cliente/${codigoCad}`, {
          params: { dataInicio, dataFim },
          signal: acHistorico.signal,
        }),
      ]);

      if (seq !== reqSeq.current) return;

      setParcelasRaw(parcelasRes?.data || { parcelas: [], totalGeral: "0,00", totalAtrasado: "0,00" });

      const hist = Array.isArray(historicoRes?.data) ? historicoRes.data : [];
      // ordena por data desc (BR)
      const ordered = [...hist].sort((a, b) => {
        const da = parseBR(a?.Data)?.getTime?.() || 0;
        const db = parseBR(b?.Data)?.getTime?.() || 0;
        return db - da;
      });
      setHistoricoChamados(ordered);
    } catch (err) {
      const canceled =
        err?.name === "CanceledError" ||
        err?.code === "ERR_CANCELED" ||
        err?.name === "AbortError";

      if (!canceled) {
        console.error("Erro ao carregar financeiro/histórico:", err?.message || err);
        // não derruba o modal; só sinaliza
      }
    } finally {
      if (seq === reqSeq.current) {
        setIsLoadingParcelas(false);
        setIsLoadingHistorico(false);
      }
    }
  }, []);

  useEffect(() => {
    const codigoCad = clienteSelecionado?.CodigoCad ? String(clienteSelecionado.CodigoCad) : "";
    if (!codigoCad) return;

    // preenche form base
    setForm((prev) => ({
      ...prev,
      razaoSocial: clienteSelecionado?.RazaoSocial || "",
      nomeFantasia: clienteSelecionado?.NomeFant || "",
      codigoCad,
    }));

    carregarFinanceiroEHistorico(codigoCad);
  }, [clienteSelecionado, carregarFinanceiroEHistorico]);

  // Parcela: filtra últimos 5 meses (janela por vencimento)
  const parcelasUlt5m = useMemo(() => {
    const lista = parcelasRaw?.parcelas || [];
    const { start, end } = rangeUltimos5Meses();

    const out = lista
      .map((p) => {
        const dt = parseBR(p?.vencimento);
        return { ...p, _vencDate: dt };
      })
      .filter((p) => p._vencDate && p._vencDate >= start && p._vencDate <= end)
      .sort((a, b) => (a._vencDate?.getTime?.() || 0) - (b._vencDate?.getTime?.() || 0));

    return out;
  }, [parcelasRaw]);

  const parcelasResumo = useMemo(() => {
    const lista = parcelasUlt5m || [];
    const atrasadas = lista.filter((p) => p.atrasado && p.situacao !== "Q").length;
    const emAberto = lista.filter((p) => !p.atrasado && p.situacao !== "Q").length;
    const pagas = lista.filter((p) => p.situacao === "Q").length;
    return { atrasadas, emAberto, pagas, total: lista.length };
  }, [parcelasUlt5m]);

  const historicoVisivel = useMemo(() => {
    const base = historicoChamados || [];
    return mostrarMaisHistorico ? base : base.slice(0, 12);
  }, [historicoChamados, mostrarMaisHistorico]);

  return (
    <div className="modal-novo-chamado-overlay">
      <div
        className="modal-novo-chamado-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="novo-chamado-titulo"
      >
        <div className="novoChamadoTitle">
          <h2 id="novo-chamado-titulo">Novo Chamado</h2>
        </div>

        <div className="top-actions">
          <button className="btn limpar" onClick={handleClear} type="button">
            Limpar
          </button>
        </div>

        <div className="linha-input-dupla">
          <div className="input-codigo">
            <input
              type="text"
              name="codigoCad"
              placeholder="Código do Cadastro"
              value={form.codigoCad}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") buscarClientesPorCodigo(form.codigoCad);
              }}
              onBlur={() => {
                if (form.codigoCad && !clienteSelecionado?.CodigoCad) {
                  buscarClientesPorCodigo(form.codigoCad);
                }
              }}
            />
          </div>

          <input
            type="text"
            name="contato"
            placeholder="Nome do Contato"
            value={form.contato}
            onChange={handleChange}
          />
        </div>

        <SelectCliente
          className="select-clientes"
          value={clienteSelecionado}
          includeInactiveSearch={false}
          autoReactivateOnSelect={false}
          onChange={(cliente) => {
            if (!cliente) {
              setClienteSelecionado(null);
              setParcelasRaw({ parcelas: [], totalGeral: "0,00", totalAtrasado: "0,00" });
              setHistoricoChamados([]);
              setHistSelecionado(null);
              setMostrarMaisHistorico(false);
              setForm((prev) => ({
                ...prev,
                razaoSocial: "",
                nomeFantasia: "",
                codigoCad: "",
              }));
              return;
            }

            const clienteNormalizado = montarClienteNormalizado(cliente);

            if (isClienteInativo(clienteNormalizado)) {
              setClienteSelecionado(null);
              setForm((prev) => ({
                ...prev,
                razaoSocial: "",
                nomeFantasia: "",
                codigoCad: "",
              }));
              showInfo(
                "Cliente inativo/desistente não está disponível para abertura de chamado.",
              );
              return;
            }

            setClienteSelecionado(clienteNormalizado);
            setForm((prev) => ({
              ...prev,
              razaoSocial: clienteNormalizado.RazaoSocial || "",
              nomeFantasia: clienteNormalizado.NomeFant || "",
              codigoCad: clienteNormalizado.CodigoCad || "",
            }));
          }}
        />

        {clienteSelecionado && (
          <section className="dados-cliente">
            <header className="cliente-header">
              <div className="header-top">
                <span className={statusClass} title={clienteSelecionado.TipCad || "Status"}>
                  {clienteSelecionado.TipCad || "Cadastro"}
                </span>

                {sistemaInfo && (
                  <span className={`badge sistema ${sistemaInfo.key}`} title={sistemaInfo.label}>
                    {sistemaInfo.label}
                  </span>
                )}
              </div>

              <div className="header-main" title={clienteSelecionado.RazaoSocial}>
                {clienteSelecionado.RazaoSocial}
              </div>
              <div className="header-sub" title={clienteSelecionado.NomeFant}>
                {clienteSelecionado.NomeFant}
              </div>
            </header>

            <div className="info-grid">
              {dadosClienteGrid.map((item, idx) => (
                <div className={`info-item ${item.span2 ? "span-2" : ""}`} key={idx}>
                  <span className="label">{item.label}</span>
                  <span className="value">{item.value}</span>
                </div>
              ))}
            </div>

            <div className="btn-detalhes">
              <button className="btn secondary" type="button" onClick={buscarPlanos}>
                Ver Planos
              </button>
            </div>
          </section>
        )}

        {clienteSelecionado && !isLoadingParcelas && parcelasResumo.atrasadas > 0 && (
          <div
            className="novo-chamado-alerta-financeiro critico"
            role="status"
          >
            <span
              className="novo-chamado-alerta-financeiro-icon"
              aria-hidden="true"
            >
              !
            </span>
            <div className="novo-chamado-alerta-financeiro-copy">
              <strong>
                Pendência financeira: {parcelasResumo.atrasadas}{" "}
                {parcelasResumo.atrasadas === 1
                  ? "parcela atrasada"
                  : "parcelas atrasadas"}
              </strong>
              <p>
                {parcelasResumo.emAberto > 0
                  ? `${parcelasResumo.emAberto} parcela(s) também constam em aberto no período. `
                  : ""}
                O chamado pode ser aberto normalmente; o aviso mantém a situação
                financeira visível antes do atendimento.
              </p>
            </div>
          </div>
        )}

        {/* Financeiro + Histórico (já visíveis) */}
        {clienteSelecionado && (
          <section className="cx-grid">
            {/* Financeiro */}
            <div className="cx-card">
              <div className="cx-head">
                <h3>Financeiro (últimos 5 meses)</h3>
                {isLoadingParcelas ? (
                  <span className="cx-subtle">Carregando…</span>
                ) : (
                  <span className="cx-subtle">
                    {parcelasResumo.total} itens •{" "}
                    <span className="chip chip-danger">{parcelasResumo.atrasadas} atrasadas</span>{" "}
                    <span className="chip chip-warning">{parcelasResumo.emAberto} em aberto</span>{" "}
                    <span className="chip chip-success">{parcelasResumo.pagas} pagas</span>
                  </span>
                )}
              </div>

              <div className="cx-body">
                {isLoadingParcelas ? (
                  <div className="cx-empty">Buscando parcelas…</div>
                ) : parcelasUlt5m.length === 0 ? (
                  <div className="cx-empty">Sem parcelas na janela dos últimos 5 meses.</div>
                ) : (
                  <div className="table-wrap">
                    <table className="mini-table">
                      <thead>
                        <tr>
                          <th>Vencimento</th>
                          <th className="t-right">Valor</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parcelasUlt5m.map((p, idx) => {
                          const venc = p._vencDate ? p._vencDate.toLocaleDateString("pt-BR") : p.vencimento;
                          const atrasada = !!p.atrasado && p.situacao !== "Q";

                          const st =
                            p.situacao === "Q" ? (
                              <span className="chip chip-success">Paga</span>
                            ) : atrasada ? (
                              <span className="chip chip-danger">Atrasada</span>
                            ) : (
                              <span className="chip chip-warning">Em aberto</span>
                            );

                          return (
                            <tr key={idx} className={atrasada ? "row-danger" : ""}>
                              <td>{venc}</td>
                              <td className="t-right">R$ {moneyBR(p.valor)}</td>
                              <td>{st}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="cx-foot">
                <div className="kv">
                  <span>Total Geral</span>
                  <b>R$ {parcelasRaw?.totalGeral || "0,00"}</b>
                </div>
                <div className="kv">
                  <span>Total Atrasado</span>
                  <b className="danger">R$ {parcelasRaw?.totalAtrasado || "0,00"}</b>
                </div>
              </div>
            </div>

            {/* Histórico */}
            <div className="cx-card">
              <div className="cx-head">
                <h3>Histórico de Chamados</h3>
                {isLoadingHistorico ? (
                  <span className="cx-subtle">Carregando…</span>
                ) : (
                  <span className="cx-subtle">{historicoChamados.length} registros (últimos 5 meses)</span>
                )}
              </div>

              <div className="cx-body">
                {isLoadingHistorico ? (
                  <div className="cx-empty">Buscando histórico…</div>
                ) : historicoChamados.length === 0 ? (
                  <div className="cx-empty">Sem chamados no período.</div>
                ) : (
                  <>
                    <div className="table-wrap">
                      <table className="mini-table clickable">
                        <thead>
                          <tr>
                            <th className="col-id">ID</th>
                            <th className="col-data">Data</th>
                            <th>Descrição</th>
                            <th className="col-status">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historicoVisivel.map((c) => {
                            const st = statusInfo(c.StatusChamado);
                            const isSel = histSelecionado?.ID === c.ID;

                            return (
                              <tr
                                key={c.ID}
                                className={isSel ? "is-selected" : ""}
                                onClick={() => setHistSelecionado(isSel ? null : c)}
                                title="Clique para ler com mais detalhe"
                              >
                                <td className="col-id">{c.ID}</td>
                                <td className="col-data">{c.Data}</td>
                                <td>
                                  <span className="ellipsis" title={c.DescrAtend || ""}>
                                    {c.DescrAtend || "—"}
                                  </span>
                                </td>
                                <td className="col-status">
                                  <span className={`chip chip-${st.tone}`}>{st.label}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="hist-actions">
                      {historicoChamados.length > 12 && (
                        <button
                          type="button"
                          className="btn link"
                          onClick={() => setMostrarMaisHistorico((v) => !v)}
                        >
                          {mostrarMaisHistorico ? "Mostrar menos" : "Mostrar mais"}
                        </button>
                      )}
                      {histSelecionado && (
                        <button type="button" className="btn link" onClick={() => setHistSelecionado(null)}>
                          Limpar leitura
                        </button>
                      )}
                    </div>

                    {histSelecionado && (
                      <div className="hist-detail">
                        <div className="detail-head">
                          <b>Chamado #{histSelecionado.ID}</b>
                          <span className={`chip chip-${statusInfo(histSelecionado.StatusChamado).tone}`}>
                            {statusInfo(histSelecionado.StatusChamado).label}
                          </span>
                        </div>

                        <div className="detail-grid">
                          <div className="row">
                            <span className="k">Data</span>
                            <span className="v">{histSelecionado.Data || "—"}</span>
                          </div>
                          <div className="row col-2">
                            <span className="k">Descrição</span>
                            <span className="v">{histSelecionado.DescrAtend || "—"}</span>
                          </div>
                          <div className="row col-2">
                            <span className="k">Solução</span>
                            <span className="v">{histSelecionado.SolucaoAtend || "—"}</span>
                          </div>
                          <div className="row">
                            <span className="k">Finalização</span>
                            <span className="v">
                              {histSelecionado.DataFim ||
                                histSelecionado.DataFinalizacao ||
                                histSelecionado.DataEncerramento ||
                                "—"}
                            </span>
                          </div>
                        </div>

                        {Array.isArray(histSelecionado.Historico) && histSelecionado.Historico.length > 0 && (
                          <div className="detail-history">
                            <b>Histórico de descrições</b>
                            <ul>
                              {histSelecionado.Historico.map((h, i) => (
                                <li key={i}>
                                  <span className="t">{h.data}</span>
                                  <span className="sep">—</span>
                                  <span className="d">{h.descricao}</span>
                                  {(h.autorNome || h.autorCodFunc) && (
                                    <span className="a"> — {h.autorNome || `#${h.autorCodFunc}`}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>
        )}

        <div className="form-grid-2">
          <select name="problema" value={form.problema} onChange={handleChange}>
            <option value="">Selecione o problema</option>
            {problemas.map((p, i) => (
              <option key={i} value={p}>
                {p}
              </option>
            ))}
          </select>

          <select name="subproblema" value={form.subproblema} onChange={handleChange}>
            <option value="">Selecione o subproblema</option>
            {subproblemas.map((s, i) => (
              <option key={i} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {nivelFunc === "ADM" ? (
          <select name="setor" value={form.setor} onChange={handleChange}>
            <option value="">Selecione o setor</option>
            {setores.map((s, i) => (
              <option key={i} value={s}>
                {s}
              </option>
            ))}
          </select>
        ) : (
          <div className="setor-readonly">
            <span>Setor</span>
            <strong>{form.setor || getUsuarioSessao().setor || "SUPORTE"}</strong>
          </div>
        )}

        <select name="urgencia" value={form.urgencia} onChange={handleChange}>
          <option value="">Selecione a urgência</option>
          <option value="0">0 - Não urgente</option>
          <option value="1">1 - Pouco urgente</option>
          <option value="2">2 - Urgente</option>
          <option value="3">3 - Muito urgente</option>
          <option value="4">4 - Emergência</option>
        </select>

        <textarea
          name="descricao"
          rows={4}
          value={form.descricao}
          onChange={handleChange}
          placeholder="Descrição do Atendimento"
          className="descricao-textarea"
        />

        <div className="checkbox-wrapper">
          <input
            type="checkbox"
            id="iniciarAtendimento"
            checked={form.emAtendimento}
            onChange={(e) => setForm((prev) => ({ ...prev, emAtendimento: e.target.checked }))}
          />
          <label htmlFor="iniciarAtendimento">Já iniciar em atendimento (irá direto para minha fila)</label>
        </div>

        <div className="checkbox-wrapper">
          <input
            type="checkbox"
            id="agendamento"
            checked={form.agendamento}
            onChange={(e) => setForm((prev) => ({ ...prev, agendamento: e.target.checked }))}
          />
          <label htmlFor="agendamento">Agendamento (entra na fila de agendamentos)</label>
        </div>

        <div className="modal-actions">
          <button className="btn incluir" onClick={handleSubmit} disabled={isSubmitting} type="button">
            {isSubmitting ? "Incluindo..." : "Incluir Chamado na Fila"}
          </button>
          <button className="btn fechar" onClick={onClose} type="button">
            Cancelar
          </button>
        </div>

        {mostrarModalPlanos && (
          <ModalPlanos onClose={() => setMostrarModalPlanos(false)} planos={planosCliente} />
        )}
      </div>
    </div>
  );
};

export default NovoChamadoModal;
